/**
 * WF-Step - Step container component for wizard forms
 *
 * Wraps step content and handles visibility/transitions.
 *
 * @example
 * <wf-step data-step="1">
 *   <h2>What's your role?</h2>
 *   <wf-options name="role" required>
 *     <wf-option value="developer">Developer</wf-option>
 *   </wf-options>
 * </wf-step>
 */

import { LitElement, html, PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

import { wfStepStyles } from './wf-step.styles.js';

@customElement('wf-step')
export class WfStep extends LitElement {
  static styles = wfStepStyles;

  /**
   * Step number (1-indexed)
   */
  @property({ type: Number, attribute: 'data-step' })
  step = 0;

  /**
   * Whether this step is currently active
   */
  @property({ type: Boolean, reflect: true })
  active = false;

  /**
   * Animation direction (forward/backward)
   */
  @property({ type: String, reflect: true })
  direction: 'forward' | 'backward' = 'forward';

  /**
   * Whether this step is leaving (for exit animation)
   */
  @property({ type: Boolean, reflect: true })
  leaving = false;

  /**
   * Skip condition expression (evaluated against form data)
   */
  @property({ type: String, attribute: 'data-skip-if' })
  skipIf = '';

  /**
   * List of field names in this step (auto-discovered)
   */
  @state()
  private _fields: string[] = [];

  render() {
    return html`
      <div class="wf-step-content" data-testid="wf-step-content">
        <slot></slot>
      </div>
    `;
  }

  firstUpdated(): void {
    this._discoverFields();
  }

  protected updated(changedProperties: PropertyValues): void {
    if (changedProperties.has('active')) {
      if (this.active) {
        this.leaving = false;
        this._focusFirstField();
      }
    }
  }

  /**
   * Discover field elements within this step
   */
  private _discoverFields(): void {
    // Light DOM: Query all field elements within this step
    const fieldElements = ['wf-options', 'wf-field', 'wf-email', 'wf-input', 'wf-textarea', 'wf-number'];
    const fields: string[] = [];

    const findFields = (el: Element): void => {
      if (fieldElements.includes(el.tagName.toLowerCase())) {
        const name = el.getAttribute('name');
        if (name) {
          fields.push(name);
        }
      }
      // Recurse into children
      Array.from(el.children).forEach(findFields);
    };

    Array.from(this.children).forEach(findFields);
    this._fields = fields;
  }

  /**
   * Focus the first focusable field in this step
   */
  private _focusFirstField(): void {
    // Delay to allow DOM updates
    requestAnimationFrame(() => {
      // Light DOM: Find first focusable element among children
      for (const el of this.children) {
        const focusable = this._findFocusable(el);
        if (focusable) {
          focusable.focus();
          return;
        }
      }
    });
  }

  private _findFocusable(el: Element): HTMLElement | null {
    // Check if element itself is focusable
    if (el instanceof HTMLElement && typeof el.focus === 'function') {
      // Skip wf-options - don't auto-focus as the focus style looks like selection
      const fieldElements = ['wf-field', 'wf-email', 'wf-input', 'wf-textarea'];
      if (fieldElements.includes(el.tagName.toLowerCase())) {
        return el;
      }
    }

    // Recurse into children
    for (const child of el.children) {
      const found = this._findFocusable(child);
      if (found) {return found;}
    }

    return null;
  }

  // ============================================
  // Public API
  // ============================================

  /**
   * Get field names in this step
   */
  get fields(): string[] {
    return [...this._fields];
  }

  /**
   * Check if step should be skipped based on form data
   */
  shouldSkip(formData: Record<string, unknown>): boolean {
    if (!this.skipIf) {return false;}

    try {
      // Simple expression evaluation
      // Supports: "fieldName === 'value'" or "!fieldName"
      const expression = this.skipIf.trim();

      // Handle negation
      if (expression.startsWith('!')) {
        const fieldName = expression.slice(1);
        return !formData[fieldName];
      }

      // Handle equality check
      if (expression.includes('===')) {
        const [left, right] = expression.split('===').map((s) => s.trim());
        const fieldValue = formData[left];
        const compareValue = right.replace(/['"]/g, ''); // Remove quotes
        return fieldValue === compareValue;
      }

      // Handle inequality check
      if (expression.includes('!==')) {
        const [left, right] = expression.split('!==').map((s) => s.trim());
        const fieldValue = formData[left];
        const compareValue = right.replace(/['"]/g, '');
        return fieldValue !== compareValue;
      }

      // Simple truthy check
      return Boolean(formData[expression]);
    } catch (error) {
      console.error('[WfStep] Error evaluating skip condition:', error);
      return false;
    }
  }

  /**
   * Show this step with animation
   */
  show(direction: 'forward' | 'backward' = 'forward'): void {
    this.direction = direction;
    this.leaving = false;
    this.active = true;
  }

  /**
   * Hide this step with animation
   */
  hide(): Promise<void> {
    return new Promise((resolve) => {
      this.leaving = true;

      const handleAnimationEnd = (): void => {
        this.active = false;
        this.leaving = false;
        this.removeEventListener('animationend', handleAnimationEnd);
        resolve();
      };

      this.addEventListener('animationend', handleAnimationEnd);

      // Fallback timeout
      setTimeout(() => {
        this.active = false;
        this.leaving = false;
        this.removeEventListener('animationend', handleAnimationEnd);
        resolve();
      }, 300);
    });
  }

  /**
   * Validate all fields in this step
   * Returns true if all fields are valid
   * Scrolls to and focuses the first invalid field if validation fails
   */
  async validate(): Promise<boolean> {
    // Light DOM: Validate all child elements
    const invalidElements: Element[] = [];
    let allValid = true;

    const validateElement = async (el: Element): Promise<void> => {
      // Check for validate method on field elements
      if ('validate' in el && typeof (el as { validate: () => boolean | Promise<boolean> }).validate === 'function') {
        const isValid = await (el as { validate: () => boolean | Promise<boolean> }).validate();
        if (!isValid) {
          // Track invalid elements
          invalidElements.push(el);
          allValid = false;
        }
      }

      // Recurse into children
      for (const child of el.children) {
        await validateElement(child);
      }
    };

    for (const el of this.children) {
      await validateElement(el);
    }

    // Scroll to and focus first invalid field
    if (invalidElements.length > 0) {
      const firstInvalid = invalidElements[0];

      // Smooth scroll to the element
      firstInvalid.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });

      // Focus the element after a brief delay for scroll to complete
      setTimeout(() => {
        if ('focus' in firstInvalid && typeof (firstInvalid as HTMLElement).focus === 'function') {
          (firstInvalid as HTMLElement).focus();
        }
      }, 100);
    }

    return allValid;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'wf-step': WfStep;
  }
}
